import { api } from '../api.ts';
import { renderGates } from '../components/gates.ts';
import { printMarkdown } from '../components/print.ts';
import { prose, renderDecisions, renderKpis, renderNotes, renderStakeholders } from '../components/records.ts';
import { renderRisks } from '../components/risks.ts';
import { renderTasks } from '../components/tasks.ts';
import { renderTimeline } from '../components/timeline.ts';
import { html, mount, raw } from '../html.ts';
import { currentIntlLocale, formatDate, formatMoney, HEALTH, options, PROJECT_STATUS, t, tk, today } from '../i18n.ts';
import { navigate, setQuery } from '../router.ts';
import { canWrite, isAdmin, state } from '../state.ts';
import { badge, confirmDialog, loading, openFormDialog, riskTone, select, statusBadge, toast, toastError } from '../ui.ts';
import { renderActivity, renderSignals } from './common.ts';
import { healthBadge, openProjectEdit } from './projects.ts';

const TABS = [
  { key: 'overview', label: tk('Überblick') },
  { key: 'tasks', label: tk('Aufgaben') },
  { key: 'risks', label: tk('Risiken') },
  { key: 'decisions', label: tk('Entscheidungen') },
  { key: 'timeline', label: tk('Zeitplan') },
  { key: 'gates', label: tk('Phasen & Gates') },
  { key: 'notes', label: tk('Notizen') },
  { key: 'stakeholders', label: tk('Stakeholder') },
  { key: 'kpis', label: tk('Kennzahlen') },
  { key: 'activity', label: tk('Verlauf') },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/** Guest section behind a tab; tabs without one are always shown. */
const TAB_SECTION: Partial<Record<TabKey, string>> = {
  tasks: 'tasks',
  risks: 'risks',
  decisions: 'decisions',
  timeline: 'phases',
  gates: 'gates',
  notes: 'notes',
  stakeholders: 'stakeholders',
  kpis: 'kpis',
};

export async function projectView(container: HTMLElement, params: Record<string, string>, query: URLSearchParams): Promise<void> {
  const projectId = params.id!;
  let project = await api.get(`/projects/${projectId}`);
  // Guests see only the sections the project shares with them (enforced by the server).
  const sees = (section: string | undefined) => !section || state.me?.role !== 'guest' || (project.guest_sections as string[]).includes(section);
  const tabs = TABS.filter((tab) => sees(TAB_SECTION[tab.key]));
  let active: TabKey = (tabs.find((tab) => tab.key === query.get('tab'))?.key ?? 'overview') as TabKey;

  function header() {
    const s = project.summary;
    return html`<header class="p-content-header pma-page-header pma-project-header" data-p-variant="compact" style="${project.color ? `--pma-project-color:${project.color}` : ''}">
      <div class="p-content-header__row">
        <div class="p-content-header__copy">
          <p class="p-content-header__eyebrow"><a href="/projects">${t('Projekte')}</a> · ${project.key}${project.client_name ? ` · ${project.client_name}` : ''}</p>
          <h1>${project.name}</h1>
          <p class="pma-project-header__meta">${statusBadge(project.status, PROJECT_STATUS)} ${healthBadge(project)}
            <span class="pma-muted">${formatDate(project.starts_on)} – ${formatDate(project.ends_on)}</span>
            ${project.budget_planned !== null ? html`<span class="pma-muted">${t('Budget')} ${formatMoney(project.budget_planned, project.currency)}</span>` : ''}</p>
        </div>
        <div class="p-content-header__actions">
        <button class="p-button p-button--ghost" type="button" data-export>${t('Export')}</button>
        ${
          canWrite()
            ? html`
                <button class="p-button" type="button" data-report>${t('Statusbericht')}</button>
                <button class="p-button" type="button" data-edit-project>${t('Bearbeiten')}</button>
                ${
                  project.archived_at
                    ? html`<button class="p-button p-button--ghost" type="button" data-archive="restore">${t('Wiederherstellen')}</button>`
                    : html`<button class="p-button p-button--ghost" type="button" data-archive="archive">${t('Archivieren')}</button>`
                }
                ${isAdmin() ? html`<button class="p-button p-button--ghost pma-danger-text" type="button" data-delete-project>${t('Löschen')}</button>` : ''}`
            : ''
        }
        </div>
      </div>
      <dl class="pma-summary" aria-label="${t('Projektstatus')}">
        ${sees('tasks') ? html`<div><dt>${t('Offene Aufgaben')}</dt><dd>${s.open_tasks}${s.overdue_tasks ? html` <span class="pma-overdue">· ${t('{n} überfällig', { n: s.overdue_tasks })}</span>` : ''}</dd></div>` : ''}
        ${sees('risks') ? html`<div><dt>${t('Offene Risiken')}</dt><dd>${s.open_risks}${s.open_risks ? html` ${badge(t('max {n}', { n: s.max_risk_score }), riskTone(s.max_risk_score))}` : ''}</dd></div>` : ''}
        ${sees('decisions') ? html`<div><dt>${t('Offene Entscheidungen')}</dt><dd>${s.pending_decisions}</dd></div>` : ''}
        ${sees('gates') ? html`<div><dt>${t('Offene Gates')}</dt><dd>${s.pending_gates}${s.next_gate_due ? html` <span class="pma-muted">· ${t('nächstes {date}', { date: formatDate(s.next_gate_due) })}</span>` : ''}</dd></div>` : ''}
      </dl>
    </header>`;
  }

  async function refreshHeader() {
    project = await api.get(`/projects/${projectId}`);
    const current = container.querySelector('.pma-project-header');
    if (current) current.outerHTML = header().value;
  }

  mount(
    container,
    html`${header()}
    <div class="p-tabs p-tabs--underline pma-tabs">
      <div class="p-tabs__list" role="tablist" aria-label="${t('Projektbereiche')}">${tabs.map(
        (tab) => html`<button class="p-tabs__tab" id="tab-${tab.key}" role="tab" type="button" aria-selected="${tab.key === active ? 'true' : 'false'}"
          aria-controls="panel" tabindex="${tab.key === active ? '0' : '-1'}" data-tab="${tab.key}">${t(tab.label)}</button>`,
      )}</div>
      <div class="p-tabs__panel" id="panel" role="tabpanel" aria-labelledby="tab-${active}" tabindex="0"></div>
    </div>`,
  );

  let panel = container.querySelector<HTMLElement>('#panel')!;

  async function overview(target: HTMLElement) {
    const [phases, gates] = await Promise.all([api.get(`/projects/${projectId}/phases`), api.get(`/projects/${projectId}/gates`)]);
    const activePhase = phases.items.find((p: any) => p.status === 'active');
    const nextGate = gates.items.find((g: any) => g.status === 'pending');
    mount(
      target,
      html`<div class="pma-grid-2">
        <section class="p-card"><div class="p-card__header"><h2>${t('Beschreibung')}</h2></div>
          <div class="p-card__body pma-prose">${project.description ? prose(project.description) : html`<p class="pma-muted">${t('Keine Beschreibung.')}</p>`}</div></section>
        <section class="p-card"><div class="p-card__header"><h2>${t('Stand')}</h2></div>
          <div class="p-card__body"><dl class="p-description-list" data-p-variant="inline">
            <dt>${t('Aktuelle Phase')}</dt><dd>${activePhase ? `${activePhase.name} (${t('bis {date}', { date: formatDate(activePhase.ends_on) })})` : '–'}</dd>
            <dt>${t('Nächstes Gate')}</dt><dd>${nextGate ? `${nextGate.code} · ${nextGate.name} (${formatDate(nextGate.due_on)})` : '–'}</dd>
            <dt>${t('Ampel')}</dt><dd>${project.health ? HEALTH[project.health] : state.me?.role === 'guest' ? '–' : `${HEALTH[project.suggested_health]} (${t('automatisch')})`}</dd>
            <dt>${t('Vorlage')}</dt><dd>${project.template_id ?? '–'}</dd>
          </dl></div></section>
        <section class="p-card pma-span-grid"><div class="p-card__header"><h2>${t('Hinweise')}</h2><span class="pma-muted pma-small">${t('regelbasiert · mit Begründung')}</span></div>
          <div class="p-card__body" data-signals></div></section>
        ${isAdmin() ? html`<section class="p-card pma-span-grid" data-sharing><div class="p-card__header"><h2>${t('Freigabe für Gäste')}</h2></div>
          <div class="p-card__body" data-sharing-body></div></section>` : ''}
        <section class="p-card pma-span-grid"><div class="p-card__header"><h2>${t('Letzte Aktivität')}</h2></div>
          <div class="p-card__body" data-activity></div></section>
      </div>`,
    );
    await Promise.all([
      renderActivity(target.querySelector('[data-activity]')!, projectId, 10),
      renderSignals(target.querySelector('[data-signals]')!, projectId),
      renderSharing(target),
    ]);
  }

  // Registers a shared project shows to guests, in display order.
  const GUEST_SECTIONS: [string, string][] = [
    ['phases', tk('Phasen')],
    ['gates', tk('Gates')],
    ['decisions', tk('Entscheidungen')],
    ['kpis', tk('Kennzahlen')],
    ['tasks', tk('Aufgaben')],
    ['risks', tk('Risiken')],
    ['notes', tk('Notizen')],
    ['stakeholders', tk('Stakeholder')],
  ];

  async function renderSharing(target: HTMLElement) {
    const body = target.querySelector<HTMLElement>('[data-sharing-body]');
    if (!body) return;
    const [{ items: shared }, org] = await Promise.all([api.get(`/projects/${projectId}/members`), api.get('/organization')]);
    const guests = org.members.filter((m: any) => m.role === 'guest' && !shared.some((s: any) => s.user_id === m.user_id));
    mount(
      body,
      html`${
        shared.length
          ? html`<ul class="pma-list">${shared.map(
              (m: any) => html`<li class="pma-list__row"><div>${m.name}<div class="pma-muted pma-small">${m.email}</div></div>
                <button class="p-button p-button--ghost p-button--sm" type="button" data-unshare="${m.user_id}">${t('Freigabe entziehen')}</button></li>`,
            )}</ul>`
          : html`<p class="pma-muted pma-small">${t('Nicht freigegeben. Gäste (z. B. Kunden) sehen dieses Projekt erst nach einer Freigabe – nur lesend, ohne Kontakte und interne Projekte.')}</p>`
      }
      ${guests.length ? html`<div class="pma-inline-add"><select class="p-select" data-p-size="sm" data-share-user aria-label="${t('Gast auswählen')}">${guests.map(
        (g: any) => html`<option value="${g.user_id}">${g.name} (${g.email})</option>`,
      )}</select><button class="p-button p-button--sm" type="button" data-share>${t('Freigeben')}</button></div>` : html`<p class="pma-muted pma-small">${t('Gäste fügst du unter Einstellungen → Organisation hinzu.')}</p>`}
      <fieldset class="p-fieldset"><legend class="p-label">${t('Für Gäste sichtbar')}</legend>
        <div class="pma-event-grid">${GUEST_SECTIONS.map(
          ([key, label]) => html`<label class="p-check pma-small"><input type="checkbox" data-guest-section="${key}"${
            (project.guest_sections as string[]).includes(key) ? raw(' checked') : ''
          }> <span>${t(label)}</span></label>`,
        )}</div>
        <p class="pma-muted pma-small">${t('Projektkopf, Überblick und Verlauf sind immer sichtbar und zeigen nur freigegebene Bereiche. Interne Notizen und Risiken bleiben standardmäßig verborgen.')}</p>
      </fieldset>`,
    );
    body.onchange = async (event) => {
      const box = (event.target as HTMLElement).closest<HTMLInputElement>('[data-guest-section]');
      if (!box) return;
      const sections = [...body.querySelectorAll<HTMLInputElement>('[data-guest-section]')].filter((b) => b.checked).map((b) => b.dataset.guestSection!);
      try {
        project = { ...project, guest_sections: (await api.patch(`/projects/${projectId}`, { guest_sections: sections })).guest_sections };
        toast(t('Sichtbarkeit für Gäste gespeichert'));
      } catch (error) {
        box.checked = !box.checked;
        toastError(error);
      }
    };
    body.onclick = async (event) => {
      const element = event.target as HTMLElement;
      try {
        const unshare = element.closest<HTMLElement>('[data-unshare]')?.dataset.unshare;
        if (unshare) await api.delete(`/projects/${projectId}/members/${unshare}`);
        if (element.closest('[data-share]')) {
          await api.post(`/projects/${projectId}/members`, { user_id: body.querySelector<HTMLSelectElement>('[data-share-user]')!.value });
        }
        if (unshare || element.closest('[data-share]')) await renderSharing(target);
      } catch (error) {
        toastError(error);
      }
    };
  }

  async function show(tab: TabKey, focus = false) {
    active = tab;
    setQuery('tab', tab === 'overview' ? null : tab);
    for (const button of container.querySelectorAll<HTMLButtonElement>('[role="tab"]')) {
      const selected = button.dataset.tab === tab;
      button.setAttribute('aria-selected', String(selected));
      button.tabIndex = selected ? 0 : -1;
      if (selected && focus) button.focus();
    }
    panel.setAttribute('aria-labelledby', `tab-${tab}`);
    // Replace the panel so listeners from the previous tab are dropped.
    const fresh = panel.cloneNode(false) as HTMLElement;
    panel.replaceWith(fresh);
    panel = fresh;
    mount(fresh, loading());
    const ctx = {
      projectId,
      projectKey: project.key as string,
      phases: [] as { id: string; label: string }[],
    };
    try {
      switch (tab) {
        case 'overview':
          return await overview(fresh);
        case 'tasks':
          ctx.phases = (await api.get(`/projects/${projectId}/phases`)).items.map((p: any) => ({ id: p.id, label: p.name }));
          return await renderTasks(fresh, ctx, new URLSearchParams(location.search));
        case 'risks':
          return await renderRisks(fresh, projectId);
        case 'decisions':
          return await renderDecisions(fresh, projectId);
        case 'timeline':
          return await renderTimeline(fresh, projectId, project);
        case 'gates':
          return await renderGates(fresh, projectId, project.currency, () => void refreshHeader());
        case 'notes':
          return await renderNotes(fresh, projectId);
        case 'stakeholders':
          return await renderStakeholders(fresh, projectId);
        case 'kpis':
          return await renderKpis(fresh, projectId);
        case 'activity':
          return await renderActivity(fresh, projectId, 100);
      }
    } catch (error) {
      fresh.innerHTML = '';
      toastError(error);
    }
  }

  const tabList = container.querySelector<HTMLElement>('[role="tablist"]')!;
  tabList.addEventListener('click', (event) => {
    const tab = (event.target as HTMLElement).closest<HTMLElement>('[data-tab]')?.dataset.tab as TabKey | undefined;
    if (tab && tab !== active) void show(tab);
  });
  tabList.addEventListener('keydown', (event) => {
    const index = tabs.findIndex((tab) => tab.key === active);
    let next = -1;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    event.preventDefault();
    void show(tabs[next]!.key, true);
  });

  container.addEventListener('click', async (event) => {
    const element = event.target as HTMLElement;
    if (element.closest('[data-export]')) {
      openExport();
    } else if (element.closest('[data-report]')) {
      void openReport();
    } else if (element.closest('[data-edit-project]')) {
      void openProjectEdit(project, () => void refreshHeader());
    } else if (element.closest('[data-archive]')) {
      const archive = element.closest<HTMLElement>('[data-archive]')!.dataset.archive === 'archive';
      try {
        await api.patch(`/projects/${projectId}`, { archived_at: archive ? new Date().toISOString() : null });
        toast(archive ? t('Projekt archiviert') : t('Projekt wiederhergestellt'));
        await refreshHeader();
      } catch (error) {
        toastError(error);
      }
    } else if (element.closest('[data-delete-project]')) {
      if (!(await confirmDialog(t('Projekt löschen?'), t('{project} wird mit allen Aufgaben, Risiken, Entscheidungen und Notizen endgültig gelöscht. Archivieren ist meist die bessere Wahl.', { project: `${project.key} · ${project.name}` })))) return;
      try {
        await api.delete(`/projects/${projectId}`);
        toast(t('Projekt gelöscht'));
        navigate('/projects');
      } catch (error) {
        toastError(error);
      }
    }
  });

  function openExport() {
    const kinds = { tasks: t('Aufgaben'), risks: t('Risiken'), decisions: t('Entscheidungen'), stakeholders: t('Stakeholder'), kpis: t('Kennzahlen') };
    openFormDialog({
      title: t('Als CSV exportieren'),
      description: t('Für Tabellenkalkulationen und Übergaben. Spalten und Werte bleiben maschinenlesbar (englische Spaltennamen, Rohwerte).'),
      fields: html`${select(t('Register'), 'kind', options(kinds, 'tasks'), { span: true })}
        ${select(t('Trennzeichen'), 'delimiter', options({ ';': t('Semikolon (Excel, deutsch)'), ',': t('Komma (international)') }, currentIntlLocale().startsWith('de') ? ';' : ','), { span: true })}`,
      submitLabel: t('Herunterladen'),
      onSubmit: async (data) => {
        const link = document.createElement('a');
        link.href = `/api/v1/projects/${projectId}/export/${data.kind}?delimiter=${encodeURIComponent(String(data.delimiter))}`;
        link.download = '';
        link.click();
      },
    });
  }

  async function openReport(days = 7) {
    try {
      const [markdown, assistant] = await Promise.all([
        api.text(`/projects/${projectId}/report?days=${days}`),
        api.get<{ features: string[] }>('/assistant').catch(() => ({ features: [] as string[] })),
      ]);
      const narrative = canWrite() && assistant.features.includes('report_narrative');
      openFormDialog({
        title: t('Statusbericht'),
        description: t('Automatisch aus Aufgaben, Gates, Risiken, Entscheidungen und Kennzahlen erzeugt (Markdown).'),
        wide: true,
        fields: html`<label class="p-field pma-span-2"><span class="p-label">${t('Bericht der letzten {n} Tage', { n: days })}</span>
          <textarea class="p-textarea pma-mono pma-small" rows="18" data-report-text>${markdown}</textarea></label>
          <div class="pma-span-2 pma-inline-add">
            ${narrative ? html`<button type="button" class="p-button p-button--sm" data-narrative>${t('Zusammenfassung schreiben (Assistent)')}</button>` : ''}
            <button type="button" class="p-button p-button--ghost p-button--sm" data-print>${t('Drucken / PDF')}</button>
            <button type="button" class="p-button p-button--ghost p-button--sm" data-download>${t('Als .md herunterladen')}</button></div>
          ${narrative ? html`<p class="pma-span-2 pma-muted pma-small">${t('Der Assistent fasst nur den Bericht zusammen; der Text landet oben im Bericht und kann vor dem Teilen bearbeitet werden.')}</p>` : ''}`,
        submitLabel: t('In Zwischenablage kopieren'),
        onOpen: (dialog) => {
          const text = dialog.querySelector<HTMLTextAreaElement>('[data-report-text]')!;
          dialog.querySelector('[data-print]')!.addEventListener('click', () => {
            try {
              printMarkdown(`${t('Statusbericht')} ${project.key} ${today()}`, text.value);
            } catch (error) {
              toastError(error);
            }
          });
          dialog.querySelector('[data-download]')!.addEventListener('click', () => {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(new Blob([text.value], { type: 'text/markdown' }));
            link.download = `${t('statusbericht')}-${project.key.toLowerCase()}-${today()}.md`;
            link.click();
            URL.revokeObjectURL(link.href);
          });
          dialog.querySelector<HTMLButtonElement>('[data-narrative]')?.addEventListener('click', async (event) => {
            const button = event.currentTarget as HTMLButtonElement;
            button.disabled = true;
            button.setAttribute('aria-busy', 'true');
            button.textContent = t('Assistent arbeitet …');
            try {
              const { summary } = await api.post<{ summary: string }>(`/projects/${projectId}/report/narrative`, { days });
              const [heading, ...rest] = text.value.split('\n');
              text.value = [heading, '', summary, ...rest].join('\n');
              button.remove();
            } catch (error) {
              toastError(error);
              button.disabled = false;
              button.textContent = t('Zusammenfassung schreiben (Assistent)');
            } finally {
              button.removeAttribute('aria-busy');
            }
          });
        },
        onSubmit: async (_data, form) => {
          await navigator.clipboard.writeText(form.querySelector<HTMLTextAreaElement>('[data-report-text]')!.value);
          toast(t('Bericht kopiert'));
        },
      });
    } catch (error) {
      toastError(error);
    }
  }

  await show(active);
}
